import { useCallback } from 'react';

import { useLogoImage } from '@app/hooks/useLogoImage';
import { useOrderable } from '@app/hooks/useOrderable';
import {
  FIELD_TYPES,
  formatNonRequiredFields,
  validateConditionalFields,
  validateFormFields,
} from '@app/utils/validateUtils';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';
import CreateView from '@app/components/common/views/CreateView';

import { CTA_TARGET_TYPE, getCTAPayload } from '.';
import { CTAForm } from './CTAForm';

const formFields = [
  { key: 'logo', type: FIELD_TYPES.IMAGE, message: 'Photo is required' },
  { key: 'heading', type: FIELD_TYPES.MULTILINGUAL, message: 'Heading is required' },
  { key: 'name', type: FIELD_TYPES.MULTILINGUAL, message: 'Name is required' },
  { key: 'subject', type: FIELD_TYPES.MULTILINGUAL, message: 'Subject is required' },
  { key: 'target_type', type: FIELD_TYPES.TEXT, message: 'Target type is required' },
];

const conditionalFields = [
  {
    key: 'external_link_url',
    type: FIELD_TYPES.TEXT,
    message: 'URL is required',
    shouldValidate: (values: GenericObject) => values.target_type === 'EXTERNAL_LINK',
  },
];

const nonRequiredFields = [
  { key: 'description', emptyValue: '', isMultilingual: true },
  { key: 'sub_heading', emptyValue: '', isMultilingual: true },
  { key: 'related_topic_id', emptyValue: null },
];

export const CreateCTA = () => {
  const { logoImage, handleChangeImageLogo } = useLogoImage();

  const { selected, handleChangeSelected, moveCard } = useOrderable();

  const validate = useCallback(
    (values: GenericObject) => {
      let mediaErrors = {};
      const errors = validateFormFields({ fields: formFields, values, image: logoImage });
      const conditionalFieldsErrors = validateConditionalFields({ fields: conditionalFields, values });
      if (values.target_type === CTA_TARGET_TYPE.MEDIA) {
        if (!selected.length) {
          mediaErrors = { media: 'Media is required' };
        }
      }

      return { ...errors, ...conditionalFieldsErrors, ...mediaErrors };
    },
    [logoImage, selected]
  );

  const transform = (values: GenericObject) => {
    formatNonRequiredFields(values, nonRequiredFields);

    return getCTAPayload(values, logoImage, selected);
  };

  return (
    <CreateView transform={transform} resourceSingular="cta" validate={validate}>
      <CTAForm
        ctaLogo={logoImage}
        selectedMedia={selected}
        moveCard={moveCard}
        handleChangeSelectedMedia={handleChangeSelected}
        title="CREATE CTA"
        handleChangeCtaLogo={handleChangeImageLogo}
      />
    </CreateView>
  );
};
